import { Grid, Box, Typography, IconButton, Button } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import { useReport } from '../context';
import type { ReactNode } from 'react';
import { handleReportTypePdf } from '../../../shared/services/reports/reportService';

interface ReportCardRow {
  label: string;
  value: ReactNode;
  color?: string;
  big?: boolean;
  bold?: boolean;
}

interface ReportCardProps {
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle: string;
  rows: ReportCardRow[];
  buttonColor: string;
  buttonBg: string;
  buttonHoverBg: string;
  onPdf?: () => void;
}

function ReportCard({ icon, iconBg, iconColor, title, subtitle, rows, buttonColor, buttonBg, buttonHoverBg, onPdf }: ReportCardProps) {
  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: 3,
        p: 3,
        minHeight: 210,
        boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'relative',
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              background: iconBg,
              color: iconColor,
              borderRadius: 2,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={16} color="#23272E" lineHeight={1.2}>{title}</Typography>
            <Typography fontSize={13} color="#6B7280">{subtitle}</Typography>
          </Box>
        </Box>
        <IconButton size="small" sx={{ color: '#A0AEC0', p: 0.5 }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box mt={2} mb={2}>
        {rows.map((row: ReportCardRow, idx: number) => (
          <Box key={idx} display="flex" alignItems="center" justifyContent="space-between" mb={idx < rows.length - 1 ? 1 : 0}>
            <Typography fontSize={15} color="#6B7280">{row.label}</Typography>
            <Typography
              fontWeight={row.bold ? 700 : 600}
              fontSize={row.big ? 22 : 15}
              color={row.color || '#23272E'}
              sx={row.big ? { letterSpacing: 1 } : {}}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button
        variant="text"
        onClick={onPdf}
        fullWidth
        startIcon={<DownloadIcon sx={{ color: buttonColor, fontSize: 20 }} />}
        sx={{
          fontWeight: 700,
          color: buttonColor,
          background: buttonBg,
          borderRadius: 2,
          minHeight: 36,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { background: buttonHoverBg },
        }}
      >
        Gerar PDF
      </Button>
    </Box>
  );
}

export function ReportSummaryCards() {
  const { summary, filters } = useReport();

  return (
    <Grid container spacing={3} mb={3}>
      <Grid item xs={12} md={4}>
        <ReportCard
          icon={<PeopleIcon sx={{ fontSize: 24 }} />}
          iconBg="#EEF4FF"
          iconColor="#3B4EFF"
          title="Relatório de Moradores"
          subtitle="Dados dos residentes"
          buttonColor="#3B4EFF"
          buttonBg="#EEF4FF"
          buttonHoverBg="#D6E3FF"
          onPdf={() => handleReportTypePdf(filters, 'moradores')}
          rows={[
            { label: 'Total de Moradores', value: summary?.residentsTotal ?? '-', big: true, bold: true },
            { label: 'Ativos', value: summary?.residentsActive ?? '-', color: '#22C55E' },
            { label: 'Inativos', value: summary?.residentsInactive ?? '-', color: '#EF4444' },
          ]}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ReportCard
          icon={<GroupAddIcon sx={{ fontSize: 24 }} />}
          iconBg="#E9FAF1"
          iconColor="#22C55E"
          title="Relatório de Visitantes"
          subtitle="Controle de acesso"
          buttonColor="#22C55E"
          buttonBg="#E9FAF1"
          buttonHoverBg="#CFF6E3"
          onPdf={() => handleReportTypePdf(filters, 'visitantes')}
          rows={[
            { label: 'Total de Visitas', value: summary?.visitorsTotal ?? '-', big: true, bold: true },
            { label: 'Este Mês', value: summary?.visitorsThisMonth ?? '-', color: '#3B4EFF' },
            { label: 'Média Diária', value: summary?.visitorsDailyAvg ?? '-', color: '#22C55E' },
          ]}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ReportCard
          icon={<EventNoteIcon sx={{ fontSize: 24 }} />}
          iconBg="#F6F0FF"
          iconColor="#A259FF"
          title="Relatório de Reservas"
          subtitle="Uso de espaços"
          buttonColor="#A259FF"
          buttonBg="#F6F0FF"
          buttonHoverBg="#E6D6FF"
          onPdf={() => handleReportTypePdf(filters, 'reservas')}
          rows={[
            { label: 'Total de Reservas', value: summary?.reservationsTotal ?? '-', big: true, bold: true },
            { label: 'Confirmadas', value: summary?.reservationsConfirmed ?? '-', color: '#22C55E' },
            { label: 'Canceladas', value: summary?.reservationsCancelled ?? '-', color: '#EF4444' },
          ]}
        />
      </Grid>
    </Grid>
  );
} 